import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Checkbox, Spin } from 'antd';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
import {
  ATTICUS_BROWSER_API,
  atticusError,
  atticusPurchasePath,
  isAtticusIdentifier,
  navigateToAtticusCallback,
} from './contracts';
import './AtticusFlow.css';

export interface AtticusAuthProps {
  isAuth: boolean;
  authResolved: boolean;
  userId: string;
  profileReady: boolean;
  showLoginModal: () => void;
  retryProfile: () => Promise<void>;
}

function AccountAccess({
  isAuth,
  authResolved,
  profileReady,
  showLoginModal,
  retryProfile,
}: AtticusAuthProps) {
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState(false);
  if (!authResolved) return <p role="status">Restoring your ScribeShadow session…</p>;
  if (!isAuth)
    return (
      <div>
        <p>
          Sign in to your ScribeShadow account, or create an account to continue. Your book uses
          your normal ScribeShadow credit balance.
        </p>
        <Button onClick={showLoginModal}>Sign in or create account</Button>
      </div>
    );
  if (profileReady) return null;
  return (
    <div>
      <p role="status">Preparing your ScribeShadow account…</p>
      {error && (
        <Alert type="error" message="Your account could not be prepared. Please try again." />
      )}
      <Button
        loading={retrying}
        onClick={async () => {
          setRetrying(true);
          setError(false);
          try {
            await retryProfile();
          } catch {
            setError(true);
          } finally {
            setRetrying(false);
          }
        }}
      >
        Retry account preparation
      </Button>
    </div>
  );
}

interface ConnectionTerms {
  csrf_token: string;
  consent_text: string;
  expires_at: string;
}
// StrictMode mounts effects twice before the binding cookie can reach the browser.
// Share only the pending request; completed consent/CSRF values are not cached.
const pendingBindings = new Map<string, Promise<ConnectionTerms>>();
function bindConnection(intentId: string): Promise<ConnectionTerms> {
  const pending = pendingBindings.get(intentId);
  if (pending) return pending;
  const request = axios
    .get<ConnectionTerms>(`${ATTICUS_BROWSER_API}/connect/${intentId}`, { withCredentials: true })
    .then((response) => response.data)
    .finally(() => {
      if (pendingBindings.get(intentId) === request) pendingBindings.delete(intentId);
    });
  pendingBindings.set(intentId, request);
  return request;
}
interface QuoteTerms {
  quote_id: string;
  external_book_id: string;
  book_title?: string;
  source_revision: string;
  source_word_count?: number;
  source_fingerprint?: string;
  target_language: string;
  maximum_credits: number;
  usable_balance: number;
  expires_at: string;
  consent_text: string;
  approved?: boolean;
}

const isLive = (expiresAt: string): boolean =>
  Number.isFinite(Date.parse(expiresAt)) && Date.parse(expiresAt) > Date.now();
function useExpiry(expiresAt?: string): boolean {
  const [, update] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const remaining = Date.parse(expiresAt) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    const timer = window.setTimeout(
      () => update((value) => value + 1),
      Math.min(remaining + 10, 2147483647)
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  return Boolean(expiresAt && isLive(expiresAt));
}

export function AtticusConnect(props: AtticusAuthProps): JSX.Element {
  const { intentId } = useParams();
  const [terms, setTerms] = useState<ConnectionTerms | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const owner = useRef(props.userId);
  owner.current = props.userId;
  const liveTerms = useExpiry(terms?.expires_at);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTerms(null);
    setConsent(false);
    setError(null);
    if (!isAtticusIdentifier(intentId)) {
      setLoading(false);
      setError('This connection request is invalid. Start again from Atticus.');
      return;
    }
    // Bind the browser before signup, so acquisition cannot be asserted by the caller.
    void bindConnection(intentId)
      .then((data) => {
        if (
          typeof data?.csrf_token !== 'string' ||
          !data.csrf_token ||
          typeof data.consent_text !== 'string' ||
          !isLive(data.expires_at)
        )
          throw new Error('Invalid connection terms.');
        if (!cancelled) setTerms(data);
      })
      .catch((error) => {
        if (!cancelled) setError(atticusError(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [intentId, retry]);
  useEffect(() => {
    setConsent(false);
  }, [props.userId]);
  const authorize = async () => {
    if (
      !terms ||
      !consent ||
      !props.profileReady ||
      !props.isAuth ||
      !isLive(terms.expires_at) ||
      busy
    )
      return;
    setBusy(true);
    setError(null);
    const authorizingOwner = props.userId;
    let navigating = false;
    try {
      const headers = await getAuthHeaders();
      if (owner.current !== authorizingOwner) return;
      const response = await axios.post(
        `${ATTICUS_BROWSER_API}/connect/${intentId}/authorize`,
        {
          csrf_token: terms.csrf_token,
          consent: true,
          response_mode: 'json',
        },
        { headers, withCredentials: true }
      );
      if (owner.current === authorizingOwner) {
        navigateToAtticusCallback(response.data?.redirect_url);
        navigating = true;
      }
    } catch (error) {
      if (owner.current === authorizingOwner) setError(atticusError(error));
    } finally {
      if (!navigating) setBusy(false);
    }
  };
  return (
    <main className="atticus-flow">
      <h1>Connect Atticus to ScribeShadow</h1>
      {loading ? <Spin tip="Loading connection request…" /> : null}
      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button disabled={busy} onClick={() => setRetry((value) => value + 1)}>
              Try again
            </Button>
          }
        />
      ) : null}
      {terms ? (
        <>
          <p>{terms.consent_text}</p>
          <AccountAccess {...props} />
          {!liveTerms ? (
            <Alert
              type="warning"
              message="This connection request has expired. Start again from Atticus."
            />
          ) : null}
          {props.isAuth && props.profileReady ? (
            <>
              <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)}>
                I authorize Atticus to send my books for AI-assisted translation and receive their
                translated content.
              </Checkbox>
              <div className="atticus-flow-actions">
                <Button
                  type="primary"
                  disabled={!consent || !liveTerms}
                  loading={busy}
                  onClick={() => void authorize()}
                >
                  Connect and return to Atticus
                </Button>
              </div>
            </>
          ) : null}
          <p className="atticus-flow-helper">
            Each translation needs your separate approval. Disconnecting stops new Atticus access;
            already accepted work continues.
          </p>
        </>
      ) : null}
      <Link to="/account/integrations">Manage connections</Link>
    </main>
  );
}

export function AtticusQuoteApproval(props: AtticusAuthProps): JSX.Element {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [terms, setTerms] = useState<QuoteTerms | null>(null);
  const [termsOwner, setTermsOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(false);
  const [retry, setRetry] = useState(0);
  const owner = useRef(props.userId);
  owner.current = props.userId;
  const liveTerms = useExpiry(terms?.expires_at);
  useEffect(() => {
    let cancelled = false;
    setTerms(null);
    setConsent(false);
    setApproved(false);
    setError(null);
    setLoading(false);
    if (!isAtticusIdentifier(quoteId)) {
      setError('This quote link is invalid. Request a fresh quote in Atticus.');
      return;
    }
    if (!props.isAuth || !props.profileReady) return;
    setLoading(true);
    void getAuthHeaders()
      .then((headers) =>
        axios.get<QuoteTerms>(`${ATTICUS_BROWSER_API}/quotes/${quoteId}`, {
          headers,
          withCredentials: true,
        })
      )
      .then((response) => {
        const data = response.data;
        if (
          data?.quote_id !== quoteId ||
          typeof data.external_book_id !== 'string' ||
          typeof data.source_revision !== 'string' ||
          typeof data.target_language !== 'string' ||
          typeof data.consent_text !== 'string' ||
          !Number.isFinite(Date.parse(data.expires_at)) ||
          !Number.isSafeInteger(data.maximum_credits) ||
          data.maximum_credits < 0 ||
          !Number.isSafeInteger(data.usable_balance) ||
          data.usable_balance < 0
        )
          throw new Error('Invalid quote terms.');
        if (!cancelled) {
          setTerms(data);
          setTermsOwner(props.userId);
          setApproved(data.approved === true);
        }
      })
      .catch((error) => {
        if (!cancelled) setError(atticusError(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, props.isAuth, props.profileReady, props.userId, retry]);
  const approve = async () => {
    if (
      !terms ||
      !props.isAuth ||
      !props.profileReady ||
      termsOwner !== props.userId ||
      !consent ||
      !isLive(terms.expires_at) ||
      busy ||
      terms.usable_balance < terms.maximum_credits
    )
      return;
    setBusy(true);
    setError(null);
    const approvingOwner = props.userId;
    try {
      const headers = await getAuthHeaders();
      if (owner.current !== approvingOwner) return;
      const response = await axios.post(
        `${ATTICUS_BROWSER_API}/quotes/${quoteId}/approve`,
        { consent: true, max_credits: terms.maximum_credits },
        { headers, withCredentials: true }
      );
      if (response.data?.approved !== true) throw new Error('Approval unavailable.');
      if (owner.current === approvingOwner) {
        setApproved(true);
        setConsent(false);
      }
    } catch (error) {
      if (owner.current === approvingOwner) setError(atticusError(error));
    } finally {
      setBusy(false);
    }
  };
  const shortfall = terms ? Math.max(0, terms.maximum_credits - terms.usable_balance) : 0;
  return (
    <main className="atticus-flow">
      <h1>Approve your Atticus translation</h1>
      <AccountAccess {...props} />
      {loading ? <Spin tip="Loading translation quote…" /> : null}
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {terms && props.isAuth && props.profileReady && termsOwner === props.userId ? (
        <>
          <dl className="atticus-flow-terms">
            <dt>Book</dt>
            <dd>{terms.book_title || terms.external_book_id}</dd>
            <dt>Source revision</dt>
            <dd>{terms.source_revision}</dd>
            <dt>Target language</dt>
            <dd>{terms.target_language}</dd>
            {terms.source_word_count !== undefined ? (
              <>
                <dt>Source words</dt>
                <dd>{terms.source_word_count.toLocaleString()}</dd>
              </>
            ) : null}
            <dt>Maximum credits</dt>
            <dd>{terms.maximum_credits.toLocaleString()}</dd>
            <dt>Usable credits</dt>
            <dd>{terms.usable_balance.toLocaleString()}</dd>
            <dt>Quote expires</dt>
            <dd>{new Date(terms.expires_at).toLocaleString()}</dd>
          </dl>
          {approved ? (
            <Alert
              type="success"
              showIcon
              message="Translation approved"
              description="Return to Atticus to start or check this translation. Your approval covers this source revision and quoted maximum only."
            />
          ) : !liveTerms ? (
            <Alert
              type="warning"
              message="This quote has expired. Request a fresh quote in Atticus."
            />
          ) : shortfall > 0 ? (
            <>
              <Alert
                type="warning"
                message={`You need ${shortfall.toLocaleString()} more credits.`}
                description="Purchase credits through ScribeShadow, then return here to review the live balance. Purchasing credits does not approve this translation."
              />
              <Button
                type="primary"
                onClick={() =>
                  navigate(
                    atticusPurchasePath(quoteId!, terms.maximum_credits, terms.usable_balance)
                  )
                }
              >
                Purchase ScribeShadow credits
              </Button>
            </>
          ) : (
            <>
              <p>{terms.consent_text}</p>
              <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)}>
                I approve this AI-assisted translation for up to{' '}
                {terms.maximum_credits.toLocaleString()} ScribeShadow credits and return its
                translated content to Atticus.
              </Checkbox>
              <Button
                type="primary"
                disabled={!consent}
                loading={busy}
                onClick={() => void approve()}
              >
                Approve translation
              </Button>
            </>
          )}
        </>
      ) : null}
      {props.isAuth && props.profileReady ? (
        <div className="atticus-flow-actions">
          <Button disabled={busy} onClick={() => setRetry((value) => value + 1)}>
            Refresh quote and balance
          </Button>
        </div>
      ) : null}
      <p className="atticus-flow-helper">
        No translation starts from this page until Atticus submits your approved quote. Request a
        fresh quote if the book or quote has changed.
      </p>
      <Link to="/account/integrations">Manage connections</Link>
    </main>
  );
}
